import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Calendar,
  Users,
  TrendingUp,
  Download,
  Edit2,
  Trash2,
  Pause,
  XCircle,
  ArrowRight,
  FilePlus,
  MoreVertical,
  PlayCircle,
  Shield,
  AlertCircle,
  Target,
  X,
} from "react-feather";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { DropdownMenu } from "../../components/DropdownMenu";
import { Colors } from "../../values/colors";
import { usePermissions } from "../../hooks";
import { Text } from "../../components/BaseComponents/styles";
import { Tabs } from "../../components/Tabs";
import { SurveyModal } from "./components/SurveyModal";
import { DownloadReportModal } from "./components/DownloadReportModal";
import { ComparisonModal } from "./components/ComparisonModal";
import { LoadingOver } from "../../components/LoadingOver";
import {
  PageBody,
  FiltersBar,
  SearchWrapper,
  SearchInput,
  CardsGrid,
  CardWrapper,
  PanelBackdrop,
  SurveyCard,
  CardTop,
  StatusBadge,
  CardTitle,
  StatsRow,
  StatBlock,
  StatLabel,
  StatValueRow,
  StatValue,
  StatUnit,
  StatDivider,
  CardDivider,
  CardBottom,
  DateRange,
  ArrowBtn,
  MenuItem,
  EmptyWrapper,
  EmptyIconCircle,
  EmptyTitle,
  EmptySubtext,
  BannerWrapper,
  BannerIcon,
  BannerBody,
  BannerSubtext,
  BannerActionBtn,
  BannerCloseBtn,
  ComparisonBarWrapper,
  ComparisonBarLabel,
  ComparisonSlot,
  ComparisonSlotText,
  ComparisonSlotClear,
  ComparisonIconSep,
  CancelCompareBtn,
  DoCompareBtn,
  CardCompareCheckbox,
  CardCompareCheckMark,
} from "./styles";
import { toast } from "react-toastify";
import { formatDateToDisplay } from "../../utils/date";
import RiskSurveyService from "../../services/RiskSurvey";
import compararIcon from "../../assets/icons/comparar-svg.svg";

const STATUS_BG = {
  "inProgress": {
    label: "Em andamento",
    color: Colors.orange
  },
  "finalized": {
    label: "Finalizada",
    color: Colors.secondaryGreen
  },
  "stopped": {
    label: "Pausada",
    color: Colors.gray
  },
  "closed": {
    label: "Fechada",
    color: Colors.darkBlue
  },
  "scheduled": {
    label: "Agendada",
    color: Colors.primaryPurple
  }
};

const normalizeDate = (dateStr) =>
  dateStr 
    ? dateStr.toString().split('T')[0].split(' ')[0] + ' 00:00:00' 
    : dateStr;

const getYears = (surveys) =>
  [...new Set(surveys.map((s) => s.year))].sort((a, b) => b - a);

const SurveyCardMenu = ({ survey, onEdit, onPause, onReactivate, onDelete, onEncerrar, onDownload, onComparison, canCompare }) => {
  const isOngoing = survey.status === "inProgress";
  const isPausada = survey.status === "stopped";
  const isScheduled = survey.status === "scheduled"
  return (
    <>
      <Text size={13} weight={700} style={{ margin: "10px 14px 4px", display: "block" }}>
        Ação da pesquisa
      </Text>
      {(!isScheduled || isPausada) && (
        <>
          <MenuItem onClick={(e) => onDownload(e)}>
            <Download size={14} />
              Download Relatório
          </MenuItem>
          {canCompare && (
            <MenuItem onClick={onComparison}>
              <img src={compararIcon} alt="Comparar" width={14} height={14} />
              Comparar
            </MenuItem>
          )}
        </>
      )}
      <MenuItem onClick={onEdit}>
        <Edit2 size={14} />
        Editar pesquisa
      </MenuItem>
      {isPausada && (
        <MenuItem variant="pause" onClick={onReactivate}>
        <PlayCircle size={14} />
        Reativar
        </MenuItem>
       )}
      {isOngoing ? (
        <>
          <MenuItem variant="pause" onClick={onPause}>
            <Pause size={14} />
            Pausar
          </MenuItem>
          <MenuItem variant="danger" onClick={onEncerrar}>
            <XCircle size={14} />
            Encerrar
          </MenuItem>
        </>
      ) : (
        <MenuItem variant="danger" onClick={onDelete}>
          <Trash2 size={14} />
          Deletar
        </MenuItem>
      )}
    </>
  );
};

const DEADLINE_BANNERS = [
  {
    key: "expired",
    maxDays: 0,
    color: Colors.red,
    title: "Sua pesquisa está vencida",
    text: (date) =>
      `O prazo da sua última avaliação, realizada em ${date}, expirou. Sua empresa está fora de conformidade com a NR-01. Realize uma nova pesquisa o quanto antes para regularizar.`,
  },
  {
    key: "oneDay",
    maxDays: 1,
    color: Colors.orange,
    title: "Sua pesquisa vence amanhã",
    text: (date) =>
      `Sua última pesquisa foi realizada em ${date}. O prazo para uma nova avaliação termina amanhã. Não deixe para depois.`,
  },
  {
    key: "sevenDays",
    maxDays: 7,
    color: Colors.yellow,
    title: "Faltam poucos dias para o vencimento",
    text: (date) =>
      `Sua última pesquisa foi realizada em ${date}. O prazo vence em menos de uma semana. Inicie uma nova pesquisa o quanto antes.`,
  },
  {
    key: "thirtyDays",
    maxDays: 30,
    color: Colors.primaryPurple,
    title: "Sua pesquisa está prestes a vencer",
    text: (date) =>
      `Sua última pesquisa foi realizada em ${date}. Falta menos de um mês para o prazo. Programe-se para realizar uma nova pesquisa em tempo hábil.`,
  },
  {
    key: "ninetyDays",
    maxDays: 90,
    color: Colors.primaryPurple,
    title: "Sua próxima avaliação se aproxima",
    text: (date) =>
      `Sua última pesquisa foi realizada em ${date}. Faltam cerca de 3 meses para o prazo. Programe-se para aplicar uma nova pesquisa com tranquilidade.`,
  },
  {
    key: "oneEightyDays",
    maxDays: 180,
    color: Colors.primaryPurple,
    title: "Já pensou na sua próxima avaliação psicossocial?",
    text: (date) =>
      `Sua última pesquisa foi realizada em ${date}. Ainda há tempo, mas vale começar a se planejar para a próxima aplicação.`,
  },
];

// Fallback caso a API não informe a periodicidade (ex.: chamada antiga em cache).
// O valor "de verdade" vem de business.psychosocial_survey_periodicity_days,
// via GET /riskSurvey -> regulatoryPeriodicityDays (backend, sem release de front).
const DEFAULT_PERIODICITY_DAYS = 730;

const getLastFinalizedSurvey = (surveys) => {
  const finalized = surveys.filter((s) => s.status === "finalized" && s.end_at);
  if (!finalized.length) return null;
  return finalized.reduce((latest, s) =>
    new Date(normalizeDate(s.end_at)) > new Date(normalizeDate(latest.end_at)) ? s : latest
  );
};

// 2.2.2/2.2.3: uma pesquisa Em Andamento suspende os avisos (agendada não
// suspende). 2.2.4: a supressão só vale enquanto o encerramento programado
// dessa pesquisa não tiver passado — se passar e ela não for encerrada, os
// avisos voltam a aparecer normalmente a partir do prazo calculado.
const hasActiveSuppression = (surveys) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return surveys.some((s) => {
    if (s.status !== "inProgress" || !s.end_at) return false;
    const scheduledEnd = new Date(normalizeDate(s.end_at));
    scheduledEnd.setHours(0, 0, 0, 0);
    return scheduledEnd >= today;
  });
};

const getDeadlineBanner = (surveys, periodicityDays = DEFAULT_PERIODICITY_DAYS) => {
  if (hasActiveSuppression(surveys)) return null;

  // 2.2.6: sem nenhuma pesquisa finalizada ainda não há data-base — sem aviso.
  const lastSurvey = getLastFinalizedSurvey(surveys);
  if (!lastSurvey) return null;

  // 2.1.1: prazo = encerramento da última finalizada + periodicidade aplicável.
  const baseDate = new Date(normalizeDate(lastSurvey.end_at));
  if (Number.isNaN(baseDate.getTime())) return null;
  baseDate.setHours(0, 0, 0, 0);

  const dueDate = new Date(baseDate);
  dueDate.setDate(dueDate.getDate() + periodicityDays);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysUntilDue = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));
  const formattedDate = formatDateToDisplay(lastSurvey.end_at, "DD/MM/YYYY");

  const config = DEADLINE_BANNERS.find((b) => daysUntilDue <= b.maxDays);
  if (!config) return null;

  return { ...config, text: config.text(formattedDate), surveyId: lastSurvey.id };
};

// Persiste qual etapa (180/90/30/7/1 dias) do aviso de vencimento já foi
// fechada pelo usuário para o ciclo (última pesquisa finalizada) atual.
// Não se aplica ao aviso de "vencido", que é insistente por definição.
//
// Não guardamos { surveyId, stage } em texto puro: como só precisamos de uma
// comparação de igualdade (é a mesma etapa de antes, ou avançou?), um hash
// unidirecional (djb2) resolve sem depender de nenhuma lib nova — não dá pra
// recuperar o survey/etapa originais a partir do que fica salvo.
const DEADLINE_BANNER_DISMISS_KEY = "psychosocial_risk_deadline_banner_dismissed";

function hashDismissKey(surveyId, stage) {
  const str = `${surveyId}:${stage}`;
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0; // djb2, 32-bit unsigned
  }
  return hash.toString(36);
}

const readDismissedHash = () => {
  try {
    return localStorage.getItem(DEADLINE_BANNER_DISMISS_KEY);
  } catch {
    return null;
  }
};

const writeDismissedHash = (surveyId, stage) => {
  try {
    localStorage.setItem(DEADLINE_BANNER_DISMISS_KEY, hashDismissKey(surveyId, stage));
  } catch {}
};

// Banner de Plano de Ação: some pro resto do dia depois que o usuário clica
// em "Ver planos de ação", volta a aparecer no dia seguinte se ainda houver
// ações atrasadas. Só é exibido quando o banner de vencimento não estiver visível.
const BANNER_KEY = 'action_plan_engagement';

function hasFreshBannerRecord() {
  try {
    const record = JSON.parse(localStorage.getItem(BANNER_KEY));
    if (!record) return false;
    const stored = new Date(record.timestamp).toDateString();
    const today = new Date().toDateString();
    return stored === today;
  } catch {
    return false;
  }
}

export const PsciossocialRisk = () => {
  const [surveys, setSurveys] = useState([]);
  const [selectedYear, setSelectedYear] = useState("all");
  const [search, setSearch] = useState("");
  const [setOptions] = useOutletContext();
  const navigate = useNavigate();
  const { checkPermissions } = usePermissions();
  const canViewActionPlan = checkPermissions("menu:actionplan");

  const [modal, setModal] = useState({ open: false, mode: null, survey: null });
  const [activePanel, setActivePanel] = useState({ id: null, align: "right" });
  const closePanel = () => setActivePanel({ id: null, align: "right" });

  const openModal = (mode, survey = null) =>
    setModal({ open: true, mode, survey });
  const closeModal = () =>
    setModal({ open: false, mode: null, survey: null });
  const changeMode = (mode) =>
    setModal((prev) => ({ ...prev, mode }));

  const [loading, setLoading] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonPdfLoading, setComparisonPdfLoading] = useState(false);
  const [tabs, setTabs] = useState([{ key: "all", title: "Todos" }]);
  const [dismissedHash, setDismissedHash] = useState(readDismissedHash);
  const [expiredBannerClosed, setExpiredBannerClosed] = useState(false);
  const [periodicityDays, setPeriodicityDays] = useState(DEFAULT_PERIODICITY_DAYS);
  const [attentionActionPlans, setAttentionActionPlans] = useState({ ongoing: 0, overdue: 0 });
  const [actionPlanBannerDismissed, setActionPlanBannerDismissed] = useState(false);

  const updateTabs = (data) => {
    const years = getYears(data);
    setTabs([
      { key: "all", title: "Todos" },
      ...years.map((y) => ({ key: y, title: String(y) })),
    ]);
  };

  const fetchSurveys = async (title) => {
    setLoading(true);
    const data = await RiskSurveyService.list(title);
    setSurveys(data.surveys);
    setPeriodicityDays(data.regulatoryPeriodicityDays ?? DEFAULT_PERIODICITY_DAYS);
    setAttentionActionPlans(data.attentionActionPlans || { ongoing: 0, overdue: 0 });
    updateTabs(data.surveys);
    setLoading(false);
  };

  useEffect(() => {
    fetchSurveys();
  }, []);

  useEffect(() => {
    setOptions({
      start: (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Text color="white" weight={700} style={{ textDecoration: "underline" }}>
            Risco Psicossocial
          </Text>
          <Text color="white" size={13}>
            {surveys.length} pesquisas encontradas
          </Text>
        </div>
      ),
      end: [
        ...(canViewActionPlan
          ? [
              <Button
                color={Colors.white}
                icon={<Shield size={18} />}
                onClick={() => navigate("/action-plan")}
                rounded
                style={{ color: Colors.primaryPurple }}
              >
                Acompanhamento do PGR
              </Button>,
            ]
          : []),
        <Button
          color={Colors.primary}
          icon={<Plus size={18} />}
          onClick={() => openModal("create")}
          rounded
        >
          Nova Pesquisa
        </Button>,
      ],
    });
    return () => setOptions(null);
  }, [surveys.length, navigate, setOptions, canViewActionPlan]);

  const filtered = surveys.filter((s) =>
    selectedYear === "all" || s.year === selectedYear
  );

  const deadlineBanner = getDeadlineBanner(surveys, periodicityDays);
  const isExpiredBanner = deadlineBanner?.key === "expired";
  const isStageDismissed =
    !!deadlineBanner &&
    !isExpiredBanner &&
    dismissedHash === hashDismissKey(deadlineBanner.surveyId, deadlineBanner.key);
  const showBanner =
    !!deadlineBanner && (isExpiredBanner ? !expiredBannerClosed : !isStageDismissed);

  const handleCloseBanner = () => {
    if (!deadlineBanner) return;
    if (isExpiredBanner) {
      // Insistente: some só até o próximo acesso à página.
      setExpiredBannerClosed(true);
    } else {
      // Fica fechado até o prazo avançar para a próxima etapa.
      writeDismissedHash(deadlineBanner.surveyId, deadlineBanner.key);
      setDismissedHash(hashDismissKey(deadlineBanner.surveyId, deadlineBanner.key));
    }
  };

  // Só um banner por vez: o de vencimento tem prioridade. O de Plano de Ação
  // só aparece quando o de vencimento não está sendo exibido.
  const showActionPlanBanner =
    !showBanner &&
    !actionPlanBannerDismissed &&
    !hasFreshBannerRecord() &&
    attentionActionPlans.overdue > 0;

  const handleBannerAction = () => {
    localStorage.setItem(BANNER_KEY, JSON.stringify({ timestamp: Date.now() }));
    navigate("/action-plan");
  };

  const toastError = (error) =>
    toast.error(error?.response?.data?.message || error?.message || "Ocorreu um erro inesperado.");

  const handleCreate = async ({ title, start_at, end_at }) => {
    try {
      await RiskSurveyService.create({ title, start_at, end_at });
      await fetchSurveys();
    } catch (error) {
      toastError(error);
    }
  };

  const handleSave = async (updated) => {
    try {
      await RiskSurveyService.update(updated.id, updated);
      await fetchSurveys();
    } catch (error) {
      toastError(error);
    }
  };

  const handlePause = async (survey) => {
    try {
      await RiskSurveyService.update(survey.id, {
        ...survey,
        start_at: normalizeDate(survey.start_at),
        end_at: normalizeDate(survey.end_at),
        status: "stopped",
      });
      await fetchSurveys();
    } catch (error) {
      toastError(error);
    }
  };

  const handleReactivate = async (survey) => {
    try {
      await RiskSurveyService.update(survey.id, {
        ...survey,
        start_at: normalizeDate(survey.start_at),
        end_at: normalizeDate(survey.end_at),
        status: "inProgress",
      });
      await fetchSurveys();
    } catch (error) {
      toastError(error);
    }
  };

  const handleDelete = async (survey) => {
    try {
      await RiskSurveyService.remove(survey.id);
      await fetchSurveys();
    } catch (error) {
      toastError(error);
    }
  };

  const handleEncerrar = async (survey) => {
    try {
      await RiskSurveyService.update(survey.id, {
        ...survey,
        start_at: normalizeDate(survey.start_at),
        end_at: normalizeDate(survey.end_at),
        status: "closed",
      });
      await fetchSurveys();
    } catch (error) {
      toastError(error);
    }
  };

  const enterComparisonMode = (survey) => {
    setComparisonMode(true);
    setSelectedForComparison(survey ? [survey] : []);
    setComparisonResult(null);
  };

  const exitComparisonMode = () => {
    setComparisonMode(false);
    setSelectedForComparison([]);
    setComparisonResult(null);
  };

  const toggleSurveySelection = (survey) => {
    setSelectedForComparison((prev) => {
      if (prev.find((s) => s.id === survey.id)) return prev.filter((s) => s.id !== survey.id);
      if (prev.length >= 2) return prev;
      return [...prev, survey];
    });
  };

  const handleDownloadComparisonPdf = async () => {
    if (selectedForComparison.length < 2 || comparisonPdfLoading) return;
    setComparisonPdfLoading(true);
    try {
      const blob = await RiskSurveyService.downloadComparePDF(
        selectedForComparison[0].id,
        selectedForComparison[1].id,
      );
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedForComparison[0].title}-vs-${selectedForComparison[1].title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(error);
    } finally {
      setComparisonPdfLoading(false);
    }
  };

  const handleDoCompare = async () => {
    if (selectedForComparison.length !== 2 || comparisonLoading) return;
    setComparisonLoading(true);
    try {
      const result = await RiskSurveyService.compare(
        selectedForComparison[0].id,
        selectedForComparison[1].id,
      );
      setComparisonResult(result.data);
    } catch (error) {
      toastError(error);
    } finally {
      setComparisonLoading(false);
    }
  };

  return (
    <>
      <Box noPadding noHeader style={{ minHeight: "80vh" }}>
        <PageBody>
          {comparisonMode && (
            <ComparisonBarWrapper>
              <ComparisonBarLabel>
                Comparar ({selectedForComparison.length}/2):
              </ComparisonBarLabel>

              <ComparisonSlot $filled={!!selectedForComparison[0]}>
                {selectedForComparison[0] ? (
                  <>
                    <ComparisonSlotText $filled>{selectedForComparison[0].title}</ComparisonSlotText>
                    <ComparisonSlotClear $filled onClick={() => toggleSurveySelection(selectedForComparison[0])}>
                      <X size={12} />
                    </ComparisonSlotClear>
                  </>
                ) : (
                  <ComparisonSlotText $empty>Selecione uma pesquisa</ComparisonSlotText>
                )}
              </ComparisonSlot>

              <ComparisonIconSep>
                <img src={compararIcon} alt="" width={18} height={18} style={{ filter: "brightness(0) invert(1)" }} />
              </ComparisonIconSep>

              <ComparisonSlot $filled={!!selectedForComparison[1]}>
                {selectedForComparison[1] ? (
                  <>
                    <ComparisonSlotText $filled>{selectedForComparison[1].title}</ComparisonSlotText>
                    <ComparisonSlotClear $filled onClick={() => toggleSurveySelection(selectedForComparison[1])}>
                      <X size={12} />
                    </ComparisonSlotClear>
                  </>
                ) : (
                  <ComparisonSlotText $empty>Selecione uma pesquisa</ComparisonSlotText>
                )}
              </ComparisonSlot>

              <CancelCompareBtn onClick={exitComparisonMode}>Cancelar</CancelCompareBtn>

              <DoCompareBtn
                disabled={selectedForComparison.length < 2 || comparisonLoading}
                onClick={handleDoCompare}
              >
                <img src={compararIcon} alt="" width={14} height={14} />
                {comparisonLoading ? "Carregando..." : "Comparar"}
              </DoCompareBtn>
            </ComparisonBarWrapper>
          )}

          <FiltersBar>
            <Tabs activeTab={selectedYear} onChange={setSelectedYear} tabs={tabs} />

            <SearchWrapper>
              <Search size={15} color={Colors.lightGray} />
              <SearchInput
                placeholder="Buscar pesquisa..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchSurveys(search);
                }}
              />
            </SearchWrapper>
          </FiltersBar>
          
          {/* Banner */}
          {showBanner && (
            <BannerWrapper $accentColor={deadlineBanner.color}>
              <BannerIcon>
                <AlertCircle size={20} color={deadlineBanner.color} />
              </BannerIcon>
              <BannerBody>
                <Text size={14} weight={700} color={deadlineBanner.color}>
                  {deadlineBanner.title}
                </Text>
                <BannerSubtext>
                  <Text size={13} color={Colors.blueMist}>
                    {deadlineBanner.text}
                  </Text>
                </BannerSubtext>
              </BannerBody>
              <BannerCloseBtn onClick={handleCloseBanner}>
                <X size={16} />
              </BannerCloseBtn>
            </BannerWrapper>
          )}

          {showActionPlanBanner && (
            <BannerWrapper>
              <BannerIcon>
                <Target size={20} color={Colors.primaryPurple} />
              </BannerIcon>
              <BannerBody>
                <Text size={14} weight={700} color={Colors.primaryPurple}>
                  Plano de Ação
                </Text>
                <BannerSubtext>
                  {!!attentionActionPlans.overdue && (
                    <Text size={13} weight={500} color={Colors.red}>
                      {attentionActionPlans.overdue} {attentionActionPlans.overdue === 1 ? "ação atrasada" : "ações atrasadas"}
                    </Text>
                  )}
                  {!!attentionActionPlans.ongoing && (
                    <>
                      <Text size={13} color={Colors.blueMist}>•</Text>
                      <Text size={13} color={Colors.blueMist}>
                        {attentionActionPlans.ongoing} {attentionActionPlans.ongoing === 1 ? "ação em execução" : "ações em execução"}
                      </Text>
                    </>
                  )}
                </BannerSubtext>
              </BannerBody>
              <BannerActionBtn onClick={handleBannerAction}>
                Ver planos de ação
                <ArrowRight size={14} />
              </BannerActionBtn>
              <BannerCloseBtn onClick={() => setActionPlanBannerDismissed(true)}>
                <X size={16} />
              </BannerCloseBtn>
            </BannerWrapper>
          )}

          {loading ? (
            <LoadingOver />
          ) : filtered.length === 0 ? (
            <EmptyWrapper>
              <EmptyIconCircle>
                <FilePlus size={36} color={Colors.primaryPurple} />
              </EmptyIconCircle>
              <EmptyTitle>Nenhuma pesquisa disponível</EmptyTitle>
              <EmptySubtext>
                Você ainda não tem nenhum mapeamento cadastrado. Gostaria de criar
                uma nova pesquisa para começar a analisar os dados da sua empresa?
              </EmptySubtext>
              <Button
                color={Colors.primary}
                icon={<Plus size={18} />}
                style={{ marginTop: 8 }}
                onClick={() => openModal("create")}
              >
                Nova Pesquisa
              </Button>
            </EmptyWrapper>
          ) : (
            <CardsGrid>
              {filtered.map((survey) => {
                const isSelected = comparisonMode && !!selectedForComparison.find((s) => s.id === survey.id);
                const isDisabled = comparisonMode && selectedForComparison.length >= 2 && !isSelected;
                return (
                  <CardWrapper key={survey.id} $panelOpen={activePanel.id === survey.id} data-cardwrapper>
                  <SurveyCard $compareSelected={isSelected}>
                    <CardTop>
                      <StatusBadge bg={STATUS_BG[survey.status].color || Colors.primaryPurple}>
                        {STATUS_BG[survey.status].label || survey.status}
                      </StatusBadge>

                      {comparisonMode ? (
                        <CardCompareCheckbox
                          $checked={isSelected}
                          $disabled={isDisabled}
                          onClick={!isDisabled ? () => toggleSurveySelection(survey) : undefined}
                        >
                          {isSelected && <CardCompareCheckMark />}
                        </CardCompareCheckbox>
                      ) : (
                        <DropdownMenu icon={<MoreVertical size={18} />} onClick={closePanel}>
                          <SurveyCardMenu
                            survey={survey}
                            onDownload={(e) => {
                              const card = e.currentTarget.closest("[data-cardwrapper]");
                              const rect = card?.getBoundingClientRect();
                              const hasSpaceRight = !rect || rect.right + 8 + 308 <= window.innerWidth;
                              setActivePanel({ id: survey.id, align: hasSpaceRight ? "right" : "left" });
                            }}
                            onEdit={() => openModal("edit", survey)}
                            onPause={() => openModal("confirmPause", survey)}
                            onDelete={() => openModal("confirmDelete", survey)}
                            onEncerrar={() => openModal("confirmEncerrar", survey)}
                            onReactivate={() => openModal("confirmReactivate", survey)}
                            onComparison={() => enterComparisonMode(survey)}
                            canCompare={canViewActionPlan}
                          />
                        </DropdownMenu>
                      )}
                    </CardTop>

                    <CardTitle>{survey.title}</CardTitle>

                    <StatsRow>
                      <StatBlock>
                        <StatLabel>
                          <Users size={14} color={Colors.primaryPurple} />
                          <Text weight={700}>Respondentes</Text>
                        </StatLabel>
                        <StatValueRow>
                          <StatValue>{survey.n_answers}</StatValue>
                          <StatUnit>pessoas</StatUnit>
                        </StatValueRow>
                      </StatBlock>

                      <StatDivider />

                      <StatBlock>
                        <StatLabel>
                          <TrendingUp size={14} color={Colors.primaryPurple} />
                          <Text weight={700}>Adesão</Text>
                        </StatLabel>
                        <StatValueRow>
                          <StatValue>{survey.percentage_completion}</StatValue>
                          <StatUnit>%</StatUnit>
                        </StatValueRow>
                      </StatBlock>
                    </StatsRow>

                    <CardDivider />

                    <CardBottom>
                      <DateRange>
                        <Calendar size={14} color={Colors.primaryPurple} />
                          <Text size={13} weight={700} style={{ marginLeft: 4 }}>
                              {formatDateToDisplay(survey.start_at, 'DD MMM YYYY')}
                          </Text>
                        <ArrowRight size={14} color={Colors.primaryPurple}/>
                        <Text size={13} weight={700} style={{ marginLeft: 4 }}>
                          {formatDateToDisplay(survey.end_at, 'DD MMM YYYY')}
                        </Text>
                      </DateRange>
                      {!comparisonMode && (
                        <ArrowBtn onClick={() => navigate(`/psciossocial-risk/${survey.id}`, { state: { survey } })}>
                          <ArrowRight size={16} color="#fff" />
                        </ArrowBtn>
                      )}
                    </CardBottom>
                  </SurveyCard>

                  {!comparisonMode && activePanel.id === survey.id && (
                    <DownloadReportModal
                      survey={survey}
                      $align={activePanel.align}
                      onClose={closePanel}
                    />
                  )}
                  </CardWrapper>
                );
              })}
            </CardsGrid>
          )}
        </PageBody>
      </Box>

      {!comparisonMode && activePanel.id !== null && (
        <PanelBackdrop onClick={closePanel} />
      )}

      <SurveyModal
        open={modal.open}
        mode={modal.mode}
        survey={modal.survey}
        onClose={closeModal}
        onChangeMode={changeMode}
        onCreate={handleCreate}
        onSave={handleSave}
        onPause={handlePause}
        onReactivate={handleReactivate}
        onDelete={handleDelete}
        onEncerrar={handleEncerrar}
      />

      <ComparisonModal
        open={comparisonResult !== null}
        surveys={selectedForComparison}
        comparison={comparisonResult?.comparison}
        olderOverallAvg={comparisonResult?.olderOverallAvg}
        newerOverallAvg={comparisonResult?.newerOverallAvg}
        actionPlansS1={comparisonResult?.actionPlansS1}
        actionPlansS2={comparisonResult?.actionPlansS2}
        onClose={() => setComparisonResult(null)}
        onDownloadPdf={handleDownloadComparisonPdf}
        downloadingPdf={comparisonPdfLoading}
      />
    </>
  );
};
